import {
  Box,
  Flex,
  Input as ThemeInput,
  InputProps as InputPropsBase,
  ThemeUIStyleObject,
} from "@powerledger/ui-component-lib";
import { kebabCase } from "lodash";
import { FC, forwardRef, ForwardRefRenderFunction, ReactNode } from "react";

import { useDebouncedInput } from "@/app/hooks";

export type InputProps = {
  prefix?: {
    outside?: boolean;
    component: FC | ReactNode | HTMLElement | string;
  };
  suffix?: {
    outside?: boolean;
    component: FC | ReactNode | HTMLElement | string;
  };
  variant?: string;
  containerSx?: ThemeUIStyleObject;
  debounce?: boolean;
  disableWhiteSpacesTrim?: boolean;
} & Omit<InputPropsBase, "prefix">;

const Component: ForwardRefRenderFunction<HTMLInputElement, InputProps> = (
  {
    suffix,
    prefix,
    variant = "input",
    onChange,
    name = "",
    onInput,
    placeholder,
    containerSx,
    sx,
    debounce = false,
    disableWhiteSpacesTrim = false,
    ...props
  },
  ref,
) => {
  const { handleFieldChange, localState, handleFieldBlur } = useDebouncedInput({
    onChange,
    name,
    value: (props.value as string) || "",
    onBlur: props.onBlur,
    shouldDebounce: debounce && (Boolean(onChange) || !onInput),
    disableWhiteSpacesTrim,
  });
  return (
    <Flex
      sx={{
        alignItems: "center",
        position: "relative",

        ...containerSx,
      }}
    >
      {prefix && (
        <Box
          sx={{
            color: "textDarker",
            fontWeight: "light",
            zIndex: 1,
            ...(prefix?.outside
              ? {
                  mr: 2,
                }
              : {
                  position: "absolute",
                  left: 2,
                }),
          }}
        >
          {prefix?.component}
        </Box>
      )}
      <ThemeInput
        as={ThemeInput}
        name={name}
        data-testid={kebabCase(name)}
        variant={variant}
        placeholder={placeholder}
        sx={{
          pl: prefix ? 4 : 3,
          pr: suffix ? 4 : 3,
          position: "relative",
          ...sx,
        }}
        // No Debouncing onInput for now
        {...(onInput
          ? {
              onInput: (e) => {
                e.currentTarget.value = e.currentTarget.value.trimStart();
                onInput(e);
              },
            }
          : {
              onChange: handleFieldChange,
              value: localState,
            })}
        {...props}
        onBlur={(e) => {
          if (onInput) {
            // For onInput: after the field loses focus, trim any leading and trailing spaces.
            const trimmedValue = e.target.value.trim();
            e.target.value = trimmedValue;
            onInput?.(e);
          }
          handleFieldBlur(e);
        }}
        ref={ref}
      />

      {suffix && (
        <Flex
          sx={{
            flexShrink: 0,
            fontWeight: "light",
            ...(suffix?.outside
              ? {
                  ml: 2,
                }
              : {
                  position: "absolute",
                  right: 2,
                }),
          }}
        >
          {suffix?.component}
        </Flex>
      )}
    </Flex>
  );
};

export const Input = forwardRef(Component);
